<template>
  <div class="w-full">
    <form
      class="z-20 flex w-full grow flex-col text-black focus:outline-none"
      @submit.prevent="onSubmit"
    >
      <div class="relative">
        <div class="mb-4">
          <UiTextInput
            ref="emailRegInputRef"
            v-model="model.email"
            type="text"
            label="Электронная почта"
            maxlength="50"
            placeholder="Электронная почта"
            :errors="
              errors.email
                ? [errors.email, ...errorMessages.email]
                : errorMessages.email
            "
            @update:model-value="clearInvalidFieldError('email')"
          />
        </div>
        <div class="mb-4">
          <UiTextInput
            v-model="model.password"
            type="password"
            is-password-toggle
            autocomplete-off
            class="mb-2"
            label="Пароль"
            maxlength="30"
            placeholder="Пароль"
            :errors="
              errors.password
                ? [errors.password, ...errorMessages.password]
                : errorMessages.password
            "
            @update:model-value="clearInvalidFieldError('password')"
          />
        </div>
        <div class="mb-4">
          <UiTextInput
            v-model="model.passwordConfirmation"
            type="password"
            is-password-toggle
            autocomplete-off
            class="mb-2"
            maxlength="30"
            label="Повторите пароль"
            placeholder="Повторите пароль"
            :errors="
              errors.passwordConfirmation
                ? [
                    errors.passwordConfirmation,
                    ...errorMessages.passwordConfirmation,
                  ]
                : errorMessages.passwordConfirmation
            "
            @update:model-value="clearInvalidFieldError('passwordConfirmation')"
          />
          <div v-if="generalErrors && generalErrors.length">
            <div
              v-for="(error, key) in generalErrors"
              :key="key"
              class="mt-1 flex text-xs text-error-500 first:-mt-1"
            >
              {{ error.message }}
            </div>
          </div>
        </div>
      </div>

      <div v-if="isSuccessMessage" class="mb-1 flex text-xs text-success-500">
        На адрес {{ emailToSend }} отправлено сообщение с инструкцией по
        активации аккаунта. Письмо должно прийти в течение 3 минут. Если письмо
        не дошло &mdash; проверьте папку "Спам"
      </div>

      <UiButton
        :class="{ 'max-w-[13.75rem] md:max-w-none': maxSizeButton }"
        :loading="loading"
        type="submit"
        primary
      >
        Зарегистрироваться
      </UiButton>
    </form>
  </div>
</template>

<script lang="ts" setup>
import { useVuelidate } from '@vuelidate/core'

import {
  email,
  required,
  helpers,
  sameAs,
  minLength,
} from '@vuelidate/validators'

import { registrationFormErrorMessages, RegistrationModel } from '@/models'

defineProps({
  maxSizeButton: { type: Boolean },
})

const { registration, clearInvalidFieldError, clearInvalidFields } =
  useRegistrationStore()
const { errors, generalErrors, isErrorsExists } = storeToRefs(
  useRegistrationStore(),
)
const { loading } = useLoading()
const { withMessage } = helpers

const model = reactive(new RegistrationModel())

const isSuccessMessage = ref(false)

const rules = {
  email: {
    required: withMessage(
      registrationFormErrorMessages.email.required,
      required,
    ),
    email: withMessage(registrationFormErrorMessages.email.email, email),
  },
  password: {
    required: withMessage(
      registrationFormErrorMessages.password.required,
      required,
    ),
    minLength: withMessage(
      registrationFormErrorMessages.password.length,
      minLength(5),
    ),
    notCyrillic: withMessage(
      registrationFormErrorMessages.password.format,
      (value: string) => !value.length || !CYRILLIC_REG_EXP.test(value),
    ),
    strongPassword: withMessage(
      registrationFormErrorMessages.password.default,
      (value: string) => !value.length || PASSWORD_REG_EXP.test(value),
    ),
  },
  passwordConfirmation: {
    required: withMessage(
      registrationFormErrorMessages.passwordConfirmation.required,
      required,
    ),
    sameAsPassword: withMessage(
      registrationFormErrorMessages.passwordConfirmation.default,
      sameAs(computed(() => model.password)),
    ),
  },
}

const v$ = useVuelidate(rules, model)
const errorMessages = computed(() => useErrorMessages(v$, rules))

const emailToSend = ref('')

const resetForm = () => {
  model.email = ''
  model.password = ''
  model.passwordConfirmation = ''

  v$.value.$reset()
}

const onSubmit = async (_payload?: Event) => {
  loading.value = true
  isSuccessMessage.value = false

  v$.value.$touch()
  if (!v$.value.$invalid) {
    await registration(model)

    if (!isErrorsExists.value) {
      emailToSend.value = model.email
      isSuccessMessage.value = true
      resetForm()
    }
  }
  loading.value = false
}

const emailRegInputRef = ref<any>(null)

onMounted(() => {
  emailRegInputRef.value?.$el.querySelector('input').focus()
})

onUnmounted(() => {
  clearInvalidFields()
})
</script>
